import { useMemo, useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAdminTopics, useUpsertTopic, useDeleteTopic, type TopicRow } from '@/lib/admin-queries';
import { toast } from '@/components/ui/Toast';
import { slugify } from '@/lib/utils';
import { Input, Field } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';

const EMPTY = { name: '', slug: '', display_order: 0, is_active: true };

export function TopicsPage() {
  const { data: rows = [], isLoading } = useAdminTopics();
  const upsert = useUpsertTopic();
  const remove = useDeleteTopic();
  const [editing, setEditing] = useState<TopicRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<TopicRow | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        cell: (r: TopicRow) => <p className="font-medium">{r.name}</p>,
      },
      {
        key: 'slug',
        header: 'Slug',
        cell: (r: TopicRow) => (
          <span className="font-mono text-xs text-fg-muted">/{r.slug}</span>
        ),
      },
      {
        key: 'display_order',
        header: 'Order',
        align: 'right' as const,
        cell: (r: TopicRow) => <span className="text-xs text-fg-muted">{r.display_order}</span>,
      },
      {
        key: 'is_active',
        header: 'Active',
        cell: (r: TopicRow) => (
          <span className={`text-xs ${r.is_active ? 'text-green-500' : 'text-fg-subtle'}`}>
            {r.is_active ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: TopicRow) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onSave(row: TopicRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Topic updated' : 'Topic created');
      setEditing(null);
      setCreating(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Topic deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  function handleNew() {
    const nextOrder = rows.length > 0 ? Math.max(...rows.map((r) => r.display_order)) + 1 : 1;
    setEditing({ ...EMPTY, display_order: nextOrder, id: undefined });
    setCreating(true);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Contact</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Topics</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Contact form topics shown in dropdown. Each service can link to a topic so its &lsquo;Start a project&rsquo; button auto-selects the right topic. {rows.length} topics.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={handleNew}>
          New topic
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No topics yet. Click New topic to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={creating ? 'New topic' : 'Edit topic'}
        description="The slug is auto-generated from the name. Inactive topics are hidden from the contact form."
        size="sm"
      >
        {editing && (
          <TopicForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => {
              setEditing(null);
              setCreating(false);
            }}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this topic?"
        description={deleting ? `"${deleting.name}" will be removed.` : ''}
        confirmLabel="Delete topic"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}

interface TopicFormProps {
  value: TopicRow;
  submitting: boolean;
  onSubmit: (v: TopicRow) => void | Promise<void>;
  onCancel: () => void;
}

function TopicForm({ value, submitting, onSubmit, onCancel }: TopicFormProps) {
  const [v, setV] = useState({ ...value });
  const [errors, setErrors] = useState<Record<string, string>>({});

  function patch<K extends keyof TopicRow>(key: K, val: TopicRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onNameBlur() {
    if (!v.slug || v.slug === slugify(value.name)) {
      patch('slug', slugify(v.name));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!v.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }
    if (!v.slug.trim()) {
      setErrors({ slug: 'Slug is required' });
      return;
    }
    onSubmit(v);
  }

  return (
    <form id="topic-form" onSubmit={handleSubmit} className="space-y-4">
      <Field id="name" label="Name" required error={errors.name}>
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            value={v.name}
            onChange={(e) => patch('name', e.target.value)}
            onBlur={onNameBlur}
            placeholder="e.g. New project"
          />
        )}
      </Field>

      <Field id="slug" label="Slug" required error={errors.slug}>
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            value={v.slug}
            onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="e.g. new-project"
          />
        )}
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="display_order" label="Order">
          {({ id }) => (
            <Input
              id={id}
              type="number"
              value={v.display_order}
              onChange={(e) => patch('display_order', Number(e.target.value))}
            />
          )}
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg">Active</span>
          <span className="mt-1 flex items-center gap-2">
            <Switch
              checked={v.is_active}
              onCheckedChange={(c) => patch('is_active', !!c)}
              id="is_active"
            />
            <span className="text-xs text-fg-muted">Show in contact form</span>
          </span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="topic-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create topic'}
        </Button>
      </div>
    </form>
  );
}
